import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Zap,
  Search,
  Clock,
  ChevronRight,
  X,
} from 'lucide-react';
import {
  useCannedResponsesStore,
  replaceVariables,
  type CannedResponse,
} from '@stores/cannedResponsesStore';

interface CannedResponsePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (content: string) => void;
  variables?: Record<string, string>;
  triggerRef?: React.RefObject<HTMLElement>;
}

export function CannedResponsePicker({
  isOpen,
  onClose,
  onSelect,
  variables = {},
  triggerRef,
}: CannedResponsePickerProps) {
  const {
    categories,
    getFilteredResponses,
    incrementUsage,
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    fetchResponses,
  } = useCannedResponsesStore();

  // #18938 — load the tenant's canned responses whenever the picker opens, so
  // they are listed and searchable (it used to show only the built-in samples).
  useEffect(() => {
    if (isOpen) void fetchResponses();
  }, [isOpen, fetchResponses]);

  const pickerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const filteredResponses = getFilteredResponses();

  // Sort by usage count for "frequently used" section
  const frequentlyUsed = [...filteredResponses]
    .sort((a, b) => b.usageCount - a.usageCount)
    .slice(0, 3);

  // Focus search input when picker opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSelect = useCallback(
    (response: CannedResponse) => {
      incrementUsage(response.id);
      const content = replaceVariables(response.content, variables);
      onSelect(content);
      onClose();
      setSearchQuery('');
      setSelectedCategory(null);
    },
    [incrementUsage, variables, onSelect, onClose, setSearchQuery, setSelectedCategory]
  );

  if (!isOpen) return null;

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full mb-2 left-0 w-96 max-h-[400px] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 dark:bg-gray-800 dark:border-gray-700"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          <span className="font-medium text-gray-900 dark:text-gray-100">Canned Responses</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search or type shortcut..."
            className="input-field pl-10 w-full text-sm"
          />
        </div>
      </div>

      {/* Category Pills */}
      <div className="scrollbar-hide px-4 py-2 border-b border-gray-200 flex gap-1.5 overflow-x-auto dark:border-gray-700">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-2 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
            selectedCategory === null
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-2 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
              selectedCategory === cat.id
                ? cat.color
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="scrollbar-thin overflow-y-auto max-h-[250px]">
        {/* Frequently Used Section */}
        {!searchQuery && !selectedCategory && frequentlyUsed.length > 0 && (
          <div className="p-2">
            <div className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
              <Clock className="h-3 w-3" />
              Frequently Used
            </div>
            {frequentlyUsed.map((response) => (
              <ResponseItem
                key={`freq-${response.id}`}
                response={response}
                isHovered={hoveredId === `freq-${response.id}`}
                onHover={() => setHoveredId(`freq-${response.id}`)}
                onLeave={() => setHoveredId(null)}
                onClick={() => handleSelect(response)}
              />
            ))}
          </div>
        )}

        {/* All Responses */}
        <div className="p-2">
          {(searchQuery || selectedCategory) && (
            <div className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
              <Search className="h-3 w-3" />
              {filteredResponses.length} result{filteredResponses.length !== 1 ? 's' : ''}
            </div>
          )}

          {filteredResponses.length === 0 ? (
            <div className="text-center py-6 text-gray-400 dark:text-gray-500">
              <p className="text-sm">No responses found</p>
            </div>
          ) : (
            filteredResponses.map((response) => (
              <ResponseItem
                key={response.id}
                response={response}
                isHovered={hoveredId === response.id}
                onHover={() => setHoveredId(response.id)}
                onLeave={() => setHoveredId(null)}
                onClick={() => handleSelect(response)}
              />
            ))
          )}
        </div>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 dark:bg-gray-900 dark:text-gray-400 dark:border-gray-700">
        Type <code className="bg-gray-200 px-1 rounded dark:bg-gray-700">/shortcut</code> in the composer to quickly insert
      </div>
    </div>
  );
}

interface ResponseItemProps {
  response: CannedResponse;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick: () => void;
}

function ResponseItem({
  response,
  isHovered,
  onHover,
  onLeave,
  onClick,
}: ResponseItemProps) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
        isHovered
          ? 'bg-primary-50 dark:bg-primary-900/30'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate dark:text-gray-100">
              {response.name}
            </span>
            <code className="text-xs text-primary-600 bg-primary-50 px-1 rounded dark:text-primary-300 dark:bg-primary-900/40">
              {response.shortcut}
            </code>
          </div>
          <p className="text-xs text-gray-500 truncate mt-0.5 dark:text-gray-400">
            {response.content.slice(0, 60)}...
          </p>
        </div>
        <ChevronRight className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform dark:text-gray-500 ${
          isHovered ? 'translate-x-1' : ''
        }`} />
      </div>
    </button>
  );
}

// Hook to detect shortcut input in a contentEditable rich-text editor.
// `onAfterInsert` is called after a shortcut is expanded so the editor can
// re-read its DOM back into state.
export function useCannedResponseShortcut(
  editorRef: React.RefObject<HTMLElement>,
  onAfterInsert: () => void,
  variables?: Record<string, string>
) {
  const { findByShortcut, incrementUsage, fetchResponses } = useCannedResponsesStore();
  const [showPicker, setShowPicker] = useState(false);

  // #18938 — load the tenant's responses once the composer mounts so typing a
  // shortcut like "/qafn0910" can resolve it (not just the built-in samples).
  useEffect(() => {
    void fetchResponses();
  }, [fetchResponses]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const editor = editorRef.current;
      if (!editor) return;

      // Check for Enter key to trigger shortcut expansion
      if (e.key === 'Enter' && !e.shiftKey) {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        // Only handle a collapsed caret sitting inside a plain text node.
        if (!range.collapsed) return;
        const node = range.startContainer;
        if (node.nodeType !== Node.TEXT_NODE) return;

        const cursorPos = range.startOffset;
        const textBeforeCursor = (node.textContent || '').slice(0, cursorPos);
        const words = textBeforeCursor.split(/\s/);
        const lastWord = words[words.length - 1];

        // Check if last word is a shortcut
        if (lastWord && (lastWord.startsWith('/') || lastWord.startsWith('#'))) {
          const response = findByShortcut(lastWord);
          if (response) {
            e.preventDefault();

            const content = replaceVariables(response.content, variables || {});

            // Select the shortcut text, then replace it with the response so the
            // edit lands on the undo stack and the caret ends after the content.
            const replaceRange = document.createRange();
            replaceRange.setStart(node, cursorPos - lastWord.length);
            replaceRange.setEnd(node, cursorPos);
            selection.removeAllRanges();
            selection.addRange(replaceRange);

            incrementUsage(response.id);
            document.execCommand('insertText', false, content);
            onAfterInsert();
          }
        }
      }

      // Toggle picker with Ctrl/Cmd + /
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShowPicker((prev) => !prev);
      }
    },
    [editorRef, findByShortcut, incrementUsage, variables, onAfterInsert]
  );

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('keydown', handleKeyDown);
      return () => editor.removeEventListener('keydown', handleKeyDown);
    }
  }, [editorRef, handleKeyDown]);

  return {
    showPicker,
    setShowPicker,
  };
}
